import type { CgOverlayValidityPeriod } from '../../types/index.js'

/**
 * Lecture des périodes de validité dans l'onglet Diffusion. La décision
 * d'antenne reste celle du moteur (`backend/src/lib/cg-overlay-validity.ts`) :
 * les périodes se cumulent, une liste vide vaut « sans limite de date ».
 */

export type PeriodStatus = 'ACTIVE' | 'UPCOMING' | 'ENDED' | 'ALWAYS'

export const PERIOD_STATUS_LABELS: Record<PeriodStatus, string> = {
  ACTIVE: 'En cours',
  UPCOMING: 'Programmée',
  ENDED: 'Terminée',
  ALWAYS: 'Toujours active',
}

const DAY_MS = 86_400_000

function ms(value: string | null): number | null {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

const parisDate = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric' })
const parisMonth = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', month: 'short' })

export function periodStatus(period: CgOverlayValidityPeriod, now = new Date()): PeriodStatus {
  const start = ms(period.startsAt)
  const end = ms(period.endsAt)
  if (start === null && end === null) return 'ALWAYS'
  if (start !== null && start > now.getTime()) return 'UPCOMING'
  if (end !== null && end < now.getTime()) return 'ENDED'
  return 'ACTIVE'
}

export function hasPermanentPeriod(periods: CgOverlayValidityPeriod[]): boolean {
  return periods.some(period => periodStatus(period) === 'ALWAYS')
}

export function periodsSummary(periods: CgOverlayValidityPeriod[], now = new Date()): string {
  if (periods.length === 0 || hasPermanentPeriod(periods)) return 'Sans limite de date : la diffusion vaut tant qu\'elle est active.'
  const count = `${periods.length} période${periods.length > 1 ? 's' : ''}`
  const statuses = periods.map(period => periodStatus(period, now))
  const next = periods
    .filter((_, index) => statuses[index] === 'UPCOMING')
    .map(period => ms(period.startsAt)!)
    .sort((a, b) => a - b)[0]
  const active = statuses.includes('ACTIVE')
  if (!active && next === undefined) return `${count} · toutes terminées : la diffusion ne sortira plus.`
  const today = active ? 'active aujourd\'hui' : 'inactive aujourd\'hui'
  return next === undefined
    ? `${count} · ${today}.`
    : `${count} · ${today}, prochaine le ${parisDate.format(new Date(next))}.`
}

export interface TimelineBar {
  index: number
  status: PeriodStatus
  /** Position et largeur en pourcentage de la frise. */
  left: number
  width: number
  openStart: boolean
  openEnd: boolean
}

export interface PeriodsTimeline {
  bars: TimelineBar[]
  /** Position de maintenant, en pourcentage, ou `null` hors frise. */
  now: number | null
  months: Array<{ label: string; left: number }>
}

/**
 * Frise commune : du premier début (ou de maintenant) à la dernière fin (au
 * moins deux mois après maintenant). Une borne absente s'étire jusqu'au bord.
 */
export function periodsTimeline(periods: CgOverlayValidityPeriod[], now = new Date()): PeriodsTimeline {
  const nowMs = now.getTime()
  const starts = periods.map(period => ms(period.startsAt)).filter((value): value is number => value !== null)
  const ends = periods.map(period => ms(period.endsAt)).filter((value): value is number => value !== null)
  const from = Math.min(nowMs, ...starts)
  const to = Math.max(nowMs + 60 * DAY_MS, ...ends, ...starts.map(start => start + 7 * DAY_MS))
  const span = Math.max(1, to - from)
  const pct = (value: number) => Math.min(100, Math.max(0, ((value - from) / span) * 100))

  const bars = periods.map((period, index) => {
    const start = ms(period.startsAt)
    const end = ms(period.endsAt)
    const left = start === null ? 0 : pct(start)
    const right = end === null ? 100 : pct(end)
    return {
      index,
      status: periodStatus(period, now),
      left,
      width: Math.max(0.6, right - left),
      openStart: start === null,
      openEnd: end === null,
    }
  })

  const months: PeriodsTimeline['months'] = []
  const cursor = new Date(from)
  cursor.setUTCDate(1)
  cursor.setUTCHours(0, 0, 0, 0)
  for (let guard = 0; cursor.getTime() <= to && guard < 36; guard++) {
    if (cursor.getTime() >= from - DAY_MS * 31) {
      months.push({ label: parisMonth.format(cursor), left: pct(cursor.getTime()) })
    }
    cursor.setUTCMonth(cursor.getUTCMonth() + 1)
  }
  return { bars, now: nowMs >= from && nowMs <= to ? pct(nowMs) : null, months }
}
